import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getRestauranteActual } from '../../api/public';
import { getImageUrl } from '../../utils/imageUrl';
import { LogIn } from 'lucide-react';

const Navbar = () => {
  const [logo, setLogo] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const restaurante = await getRestauranteActual();

        if (restaurante?.logo) {
          const baseUrl = getImageUrl(restaurante.logo);
          setLogo(`${baseUrl}?t=${Date.now()}`);
        }
      } catch (error) {
        console.error('Error cargando logo en Navbar:', error);
      }
    };

    cargarLogo();
  }, []);

  // Detectar scroll para ocultar/mostrar navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const menuItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Menú', path: '/menu' },
    { name: 'Promociones', path: '/promociones' },
    { name: 'Restaurantes', path: '/restaurantes' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const { isAuthenticated } = useAuth();

  return (
    <nav className={`bg-white shadow-md fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Fila única del Header: Logo + Opciones de Módulos + Botón Login */}
        <div className="flex items-center justify-between h-14 sm:h-16 gap-1.5 sm:gap-4">
          {/* Logo (Izquierda) */}
          <Link to="/" className="flex items-center shrink-0 pr-1 sm:pr-2">
            {logo ? (
              <img src={logo} alt="Logo" className="h-10 sm:h-13 w-auto object-contain transition-transform" />
            ) : (
              <span className="font-extrabold text-xl sm:text-2xl text-red-600 tracking-tight">ASADERO</span>
            )}
          </Link>

          {/* Opciones de Módulos (Centro / En la misma fila horizontal) */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-5 overflow-x-auto no-scrollbar scroll-smooth min-w-0 flex-1 justify-start md:justify-center px-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`shrink-0 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-150 whitespace-nowrap ${
                    isActive
                      ? 'bg-red-50 text-red-600 font-bold border border-red-200 shadow-xs'
                      : 'text-gray-600 hover:text-red-600 hover:bg-gray-100/80'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Botón de Login (Derecha) */}
          <div className="shrink-0 flex items-center">
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
              title="Iniciar Sesión"
              aria-label="Iniciar Sesión"
              className="bg-red-600 text-white p-2 sm:px-4 sm:py-2 rounded-full hover:bg-red-700 transition-all font-semibold shadow-xs flex items-center justify-center gap-1.5 text-xs sm:text-sm cursor-pointer"
            >
              <LogIn className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Iniciar Sesión</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
